import { Droplet } from 'lucide-react';

import type { PoiHistoryEvent } from '@/api/poiHistoryApi';
import {
    getLeakStatusPillPresentation,
    getPoiStatusPillPresentation,
    type StatusPillPresentation,
} from '@/components/shared/statusPillPresentation';

// History pills reuse the designed pills from the entity-status registries — icon and
// colours alike — so the timeline speaks the same visual language as the rest of the
// portal, while keeping the history's own point-in-time wording ("Leak Found" as recorded
// then, not the current "Asmt. Complete – Leak Found"). Assessment events are POI-family:
// they take the POI sheet's circled-outcome pills wherever the sheet has an entry for the
// recorded outcome, and fall back to the leak sheet's droplet pill for outcomes only the
// leak sheet designs (Leak Repaired, Already Marked Up). Leak, repair and recheck events
// are leak-family throughout. Colour families follow the registries' own semantics:
// green = repaired/clear, red = no leak/failed, yellow = inconclusive, orange =
// unable/unsuitable, blue = leak present, solid blue = already marked up. Outcomes with
// no registry analogue (Closed, Digging) take the neutral bordered treatment rather than
// a colour the design pack never specified.

function withLabel(presentation: StatusPillPresentation, label: string): StatusPillPresentation {
    return { ...presentation, label };
}

/** Leak-family outcome with no designed colour: neutral chip, family droplet icon. */
function neutralPill(label: string): StatusPillPresentation {
    return { label, icon: Droplet, background: '', color: '', neutral: true };
}

// Keyed by the server's summary labels — the wire contract documented on PoiHistoryEvent.
const PILLS_BY_EVENT_TYPE: Record<
    PoiHistoryEvent['eventType'],
    Partial<Record<string, () => StatusPillPresentation>>
> = {
    poi_assessment: {
        'Awaiting Assessment': () => getPoiStatusPillPresentation('awaiting_assessment'),
        // The POI sheet's "Asmt. Complete – Leak Found" pill (circled tick) under the
        // outcome's historical name.
        'Leak Found': () => withLabel(getPoiStatusPillPresentation('leak_found'), 'Leak Found'),
        // No POI-sheet entry — the POI status stays "Leak Found" once repaired — so this is
        // the leak sheet's Repaired pill, the only designed "repaired" treatment.
        'Leak Repaired': () => withLabel(getLeakStatusPillPresentation('repaired'), 'Leak Repaired'),
        'No Leak Found': () => withLabel(getPoiStatusPillPresentation('no_leak_found'), 'No Leak Found'),
        Inconclusive: () => withLabel(getPoiStatusPillPresentation('inconclusive'), 'Inconclusive'),
        // No POI-sheet entry either (Already Marked Up presents as Leak Found on the POI
        // side), so the leak sheet's solid-blue pill carries these words.
        'Already Marked Up': () => withLabel(getLeakStatusPillPresentation('already_marked_up'), 'Already Marked Up'),
        // The server writes "Unable to Check"; the pill shows the sheet's "Unable To Check".
        'Unable to Check': () => getPoiStatusPillPresentation('unable_to_check'),
    },
    nodig_leak: {
        Open: () => withLabel(getLeakStatusPillPresentation('leak_found'), 'Open'),
        Fixed: () => withLabel(getLeakStatusPillPresentation('repaired'), 'Fixed'),
        Unsuitable: () => getLeakStatusPillPresentation('unsuitable'),
        Closed: () => neutralPill('Closed'),
    },
    nodig_repair: {
        Repaired: () => getLeakStatusPillPresentation('repaired'),
        'Not Repaired': () => withLabel(getLeakStatusPillPresentation('initial_repair_failed'), 'Not Repaired'),
        Unsuitable: () => getLeakStatusPillPresentation('unsuitable'),
    },
    nodig_recheck: {
        Noise: () => withLabel(getLeakStatusPillPresentation('recheck_failed'), 'Noise'),
        'No Noise': () => withLabel(getLeakStatusPillPresentation('rechecked'), 'No Noise'),
        Inconclusive: () => withLabel(getLeakStatusPillPresentation('inconclusive'), 'Inconclusive'),
        Digging: () => neutralPill('Digging'),
        'Unable to Isolate': () => withLabel(getLeakStatusPillPresentation('unable_to_check'), 'Unable to Isolate'),
    },
};

/**
 * The pill for a history event's recorded outcome, or null when the event carries none
 * (a repair with nothing recorded yet). An unrecognised summary renders as a neutral
 * pill with the server's own wording — shown honestly, never coloured by guesswork.
 */
export function getPoiHistoryEventPillPresentation(event: PoiHistoryEvent): StatusPillPresentation | null {
    if (event.summary == null || event.summary === '') return null;
    const pill = PILLS_BY_EVENT_TYPE[event.eventType][event.summary];
    return pill ? pill() : neutralPill(event.summary);
}
